/**
 * AuthModal — Phase 6
 *
 * Full-screen overlay shown before the game loads when no valid session token
 * exists. Provides Register and Login tabs. On success calls onSuccess(token).
 *
 * Does NOT use the base Modal component — it is a full-page cover, not an
 * in-game dialog. Styled minimally with inline CSS to avoid depending on
 * components.css classes that may not be loaded yet.
 */

import { apiRegister, apiLogin, saveToken } from '../../core/ServerConnection.js';
import { companySetupFieldsHtml, bindCompanySetupFields } from '../components/CompanySetupFields.js';
import { pickCompanyName } from '../../config/companyNames.js';
import { COMPANY_COLOR_PALETTE } from '../../config/constants.js';
import { STARTING_PORT_IDS } from '../../config/ports.js';

export class AuthModal {
  /**
   * @param {function(string, boolean): void} onSuccess  Called with the session
   *   token and the account's isAdmin flag. Usually false for a freshly
   *   registered account, but not always — an ADMIN_USERNAMES allowlist
   *   match (server/auth/accounts.js) can grant admin at registration too.
   */
  constructor(onSuccess) {
    this._onSuccess = onSuccess;
    this._activeTab = 'login';
    this._el = null;
    this._initialError = null;
    // Company setup (name/color/starting port) — lazily initialized the
    // first time the register tab renders (see _render()), then preserved
    // across re-renders (tab switches back and forth) so the player's
    // in-progress choices aren't silently reset.
    this._registerSetup = null;
    this._create();
  }

  /**
   * Show the auth screen with an error already visible — used when
   * main.js's boot sequence falls back here after a failed/expired OIDC
   * exchange, so the player sees why they're back at the login screen
   * instead of it looking like a silent failure.
   */
  show(initialError) {
    this._initialError = initialError || null;
    document.body.appendChild(this._el);
    this._render();
  }

  hide() {
    if (this._el && this._el.parentNode) {
      this._el.parentNode.removeChild(this._el);
    }
  }

  // ---------- DOM setup ----------

  _create() {
    this._el = document.createElement('div');
    this._el.id = 'auth-overlay';
    this._el.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:9999',
      'background:var(--bg-dark, #0a0e1a)',
      'display:flex', 'align-items:center', 'justify-content:center',
      'font-family:inherit'
    ].join(';');
  }

  _render() {
    const isLogin = this._activeTab === 'login';
    if (!isLogin && !this._registerSetup) {
      this._registerSetup = {
        companyName: pickCompanyName(),
        companyColor: COMPANY_COLOR_PALETTE[Math.floor(Math.random() * COMPANY_COLOR_PALETTE.length)],
        startPortId: STARTING_PORT_IDS[0]
      };
    }
    this._el.innerHTML = `
      <div class="auth-card" style="
        background:var(--bg-panel, #111827);
        border:1px solid var(--border, #1e2d45);
        border-radius:8px;
        padding:2rem;
        width:min(420px, 92vw);
        box-shadow:0 8px 40px rgba(0,0,0,.6);
      ">
        <h2 style="margin:0 0 1.5rem;color:var(--accent, #3498db);font-size:1.4rem;text-align:center">
          SeaTrader
        </h2>

        <div class="auth-tabs" style="display:flex;gap:.5rem;margin-bottom:1.5rem">
          <button data-tab="login" class="auth-tab ${isLogin ? 'auth-tab-active' : ''}" style="
            flex:1;padding:.6rem;border:1px solid var(--border,#1e2d45);border-radius:4px;
            background:${isLogin ? 'var(--accent,#3498db)' : 'transparent'};
            color:${isLogin ? '#fff' : 'var(--text-muted,#8899aa)'};
            cursor:pointer;font-size:.9rem">Login</button>
          <button data-tab="register" class="auth-tab ${!isLogin ? 'auth-tab-active' : ''}" style="
            flex:1;padding:.6rem;border:1px solid var(--border,#1e2d45);border-radius:4px;
            background:${!isLogin ? 'var(--accent,#3498db)' : 'transparent'};
            color:${!isLogin ? '#fff' : 'var(--text-muted,#8899aa)'};
            cursor:pointer;font-size:.9rem">Register</button>
        </div>

        <form id="auth-form" autocomplete="on">
          <div style="margin-bottom:1rem">
            <label style="display:block;margin-bottom:.4rem;color:var(--text,#cdd6e0);font-size:.85rem">
              Username
            </label>
            <input id="auth-username" type="text" autocomplete="username"
              style="width:100%;box-sizing:border-box;padding:.55rem .7rem;
                background:var(--bg-input,#0d1117);color:var(--text,#cdd6e0);
                border:1px solid var(--border,#1e2d45);border-radius:4px;font-size:.9rem"
              placeholder="Username" required minlength="3" maxlength="30">
          </div>
          <div style="margin-bottom:${isLogin ? '1.4rem' : '1rem'}">
            <label style="display:block;margin-bottom:.4rem;color:var(--text,#cdd6e0);font-size:.85rem">
              Password
            </label>
            <input id="auth-password" type="password" autocomplete="${isLogin ? 'current-password' : 'new-password'}"
              style="width:100%;box-sizing:border-box;padding:.55rem .7rem;
                background:var(--bg-input,#0d1117);color:var(--text,#cdd6e0);
                border:1px solid var(--border,#1e2d45);border-radius:4px;font-size:.9rem"
              placeholder="Password" required minlength="6">
          </div>
          ${!isLogin ? `
          <div style="margin-bottom:1.4rem">
            <label style="display:block;margin-bottom:.4rem;color:var(--text,#cdd6e0);font-size:.85rem">
              Repeat Password
            </label>
            <input id="auth-password2" type="password" autocomplete="new-password"
              style="width:100%;box-sizing:border-box;padding:.55rem .7rem;
                background:var(--bg-input,#0d1117);color:var(--text,#cdd6e0);
                border:1px solid var(--border,#1e2d45);border-radius:4px;font-size:.9rem"
              placeholder="Repeat password" required minlength="6">
          </div>
          ${companySetupFieldsHtml('auth-setup', this._registerSetup)}` : ''}

          <div id="auth-error" style="
            display:none;margin-bottom:1rem;padding:.55rem .7rem;
            background:rgba(231,76,60,.15);border:1px solid rgba(231,76,60,.4);
            border-radius:4px;color:#e74c3c;font-size:.85rem">
          </div>

          <button type="submit" id="auth-submit" style="
            width:100%;padding:.7rem;
            background:var(--accent,#3498db);color:#fff;
            border:none;border-radius:4px;font-size:1rem;cursor:pointer">
            ${isLogin ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        <p style="margin:.8rem 0 0;text-align:center;font-size:.8rem;color:var(--text-muted,#8899aa)">
          ${isLogin
            ? 'New to SeaTrader? <a href="#" id="auth-switch" style="color:var(--accent,#3498db)">Register</a>'
            : 'Already have an account? <a href="#" id="auth-switch" style="color:var(--accent,#3498db)">Sign in</a>'
          }
        </p>

        <div style="display:flex;align-items:center;gap:.7rem;margin:1.2rem 0">
          <div style="flex:1;height:1px;background:var(--border,#1e2d45)"></div>
          <span style="font-size:.75rem;color:var(--text-muted,#8899aa)">or</span>
          <div style="flex:1;height:1px;background:var(--border,#1e2d45)"></div>
        </div>

        <button type="button" id="auth-google" style="
          width:100%;padding:.6rem;display:flex;align-items:center;justify-content:center;gap:.5rem;
          background:#fff;color:#3c4043;border:1px solid var(--border,#1e2d45);
          border-radius:4px;font-size:.9rem;cursor:pointer">
          <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.9c1.7-1.57 2.7-3.88 2.7-6.62z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.84.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 0 0 9 18z"/>
            <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 0 1 3.67 9c0-.59.1-1.17.28-1.7V4.97H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.03l2.99-2.33z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.97l2.99 2.33C4.66 5.17 6.65 3.58 9 3.58z"/>
          </svg>
          Sign in with Google
        </button>
      </div>`;

    // Tab switches
    this._el.querySelectorAll('.auth-tab').forEach(btn => {
      btn.addEventListener('click', () => {
        this._activeTab = btn.dataset.tab;
        this._render();
      });
    });

    // Switch link
    const switchLink = this._el.querySelector('#auth-switch');
    if (switchLink) {
      switchLink.addEventListener('click', (e) => {
        e.preventDefault();
        this._activeTab = isLogin ? 'register' : 'login';
        this._render();
      });
    }

    if (!isLogin) {
      bindCompanySetupFields('auth-setup', this._el, this._registerSetup);
    }

    // Form submit
    const form = this._el.querySelector('#auth-form');
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      this._submit();
    });

    // Google sign-in — a real page navigation (not fetch): the server
    // handles the entire OIDC round-trip and redirects back with a
    // one-time exchange code, picked up by main.js's boot sequence.
    this._el.querySelector('#auth-google').addEventListener('click', () => {
      window.location.href = '/api/auth/oidc/start';
    });

    if (this._initialError) {
      this._showError(this._initialError);
      this._initialError = null; // one-shot — don't re-show on tab switches
    }
  }

  async _submit() {
    const username = this._el.querySelector('#auth-username').value.trim();
    const password = this._el.querySelector('#auth-password').value;
    const errorEl  = this._el.querySelector('#auth-error');
    const submitBtn = this._el.querySelector('#auth-submit');

    errorEl.style.display = 'none';
    submitBtn.disabled = true;
    submitBtn.textContent = 'Please wait…';

    try {
      let result;
      if (this._activeTab === 'register') {
        const password2 = this._el.querySelector('#auth-password2')?.value;
        if (password !== password2) {
          this._showError('Passwords do not match.');
          submitBtn.disabled = false;
          submitBtn.textContent = 'Create Account';
          return;
        }
        result = await apiRegister(username, password, {
          companyName:  this._registerSetup.companyName.trim() || undefined,
          companyColor: this._registerSetup.companyColor,
          startPortId:  this._registerSetup.startPortId
        });
      } else {
        result = await apiLogin(username, password);
      }

      if (!result.success) {
        this._showError(result.error || 'Authentication failed. Please try again.');
        submitBtn.disabled = false;
        submitBtn.textContent = this._activeTab === 'login' ? 'Sign In' : 'Create Account';
        return;
      }

      saveToken(result.token);
      this._onSuccess(result.token, !!result.isAdmin);

    } catch (err) {
      console.error('[AuthModal] Network error:', err);
      this._showError('Network error — is the server running?');
      submitBtn.disabled = false;
      submitBtn.textContent = this._activeTab === 'login' ? 'Sign In' : 'Create Account';
    }
  }

  _showError(msg) {
    const el = this._el.querySelector('#auth-error');
    if (el) { el.textContent = msg; el.style.display = 'block'; }
  }
}
